import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Form, Button, Card, Alert, Spinner
} from 'react-bootstrap';
import { useNavigate, useParams } from "react-router-dom";
import { driversCompanyAPI } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

function DriverCompanyForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    nicname: '',
    company_name: '',
    email: '',
    password: '',
    confirm_password: '',
    street_address: '',
    addressone: '',
    postal_code: '',
    title: '',
    telphone: '',
    website: '',
    tax_number: '',
    local_court: '',
    responsible_person: '',
    driver_name: '',
    phone_number: '',
    vehicle_number: '',
    vehicle_type: '',
    max_weight: '',
    max_length: '',
    max_width: '',
    max_height: ''
  });

  // جلب بيانات السائق في حالة التعديل
  useEffect(() => {
    if (isEditMode && id) {
      fetchDriverData();
    }
  }, [id, isEditMode]);


const fetchDriverData = async () => {
  try {
    setLoading(true);
    const response = await driversCompanyAPI.show(id);
    
    console.log('Edit Mode - API Response:', response); // للتصحيح
    
    if (response.success) {
      const driverData = response.data;
      console.log('Edit Mode - Driver Data:', driverData);
      
      setFormData({
        firstname: driverData.firstname || '',
        lastname: driverData.lastname || '',
        nicname: driverData.nicname || '',
        company_name: driverData.company_name || '',
        email: driverData.email || '',
        password: '',
        confirm_password: '',
        street_address: driverData.street_address || '',
        addressone: driverData.addressone || '',
        postal_code: driverData.postal_code || '',
        title: driverData.title || '',
        telphone: driverData.telphone || '',
        website: typeof driverData.website === 'object' ? '' : (driverData.website || ''),
        tax_number: driverData.tax_number || '',
        local_court: driverData.local_court || '',
        responsible_person: driverData.responsible_person || '',
        driver_name: driverData.driver_name || '',
        phone_number: driverData.phone_number || '',
        vehicle_number: driverData.vehicle_number || '',
        vehicle_type: driverData.vehicle_type || '',
        max_weight: driverData.max_weight || '',
        max_length: driverData.max_length || '',
        max_width: driverData.max_width || '',
        max_height: driverData.max_height || ''
      });
    } else {
      setError(response.message || t('failed_to_fetch_driver'));
    }
  } catch (err) {
    setError(t('failed_to_fetch_driver'));
    console.error('Error fetching driver:', err);
  } finally {
    setLoading(false);
  }
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.firstname.trim()) {
      setError(t('first_name_required'));
      return false;
    }
    if (!formData.lastname.trim()) {
      setError(t('last_name_required'));
      return false;
    }
    if (!formData.company_name.trim()) {
      setError(t('company_name_required'));
      return false;
    }
    if (!formData.email.trim()) {
      setError(t('email_required'));
      return false;
    }
    if (!isEditMode && !formData.password) {
      setError(t('password_required'));
      return false;
    }
    if (!isEditMode && formData.password !== formData.confirm_password) {
      setError(t('passwords_do_not_match'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    if (!validateForm()) {
      setSaving(false);
      return;
    }

    try {
      let response;
      
      const dataToSend = { ...formData };
      
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === '' || dataToSend[key] == null) {
          delete dataToSend[key];
        }
      });
      
      if (isEditMode) {
        if (!dataToSend.password) {
          delete dataToSend.password;
          delete dataToSend.confirm_password;
        }
        
        response = await driversCompanyAPI.update(id, dataToSend);
      } else {
        response = await driversCompanyAPI.store(dataToSend);
      }

      console.log('API Response:', response);

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: isEditMode ? t('driver_updated_successfully') : t('driver_created_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        
        navigate('/admin/drivers-company');
      } else {
        setError(response.message || (isEditMode ? t('failed_to_update_driver') : t('failed_to_create_driver')));
        console.error('API Error:', response);
      }
    } catch (err) {
      setError(isEditMode ? t('failed_to_update_driver') : t('failed_to_create_driver'));
      console.error('Error saving driver:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold mb-0">
          {isEditMode ? t('Edit') : t('Add')}
        </h3>
        <Button 
          variant="secondary" 
          onClick={() => navigate('/admin/drivers-company')}
          disabled={saving}
        >
          {t('back_to_list')}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <h4 className="mb-3">{t('driver_information')}</h4>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('company_name')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('first_name')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('last_name')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('nickname')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="nicname"
                    value={formData.nicname}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('email')} *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('title')}</Form.Label>
                  <Form.Select
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('select_title')}</option>
                    <option value="Mr">Mr</option>
                    <option value="Ms">Ms</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {!isEditMode && (
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('Password')} *</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={8}
                    />
                    <Form.Text muted>{t('password_min_length')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('confirm_password')} *</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('phone')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="telphone"
                    value={formData.telphone}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('website')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('tax_number')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="tax_number"
                    value={formData.tax_number}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('local_court')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="local_court"
                    value={formData.local_court}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('responsible_person')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="responsible_person"
                    value={formData.responsible_person}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h4 className="mb-3 mt-4">{t('address_information')}</h4>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('street_address')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('addressone')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="addressone"
                    value={formData.addressone}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                {/* <Form.Group className="mb-3" controlId="formPostalCode">
                  <Form.Label>{t('postal_code')}</Form.Label>
                  <LocationAutocomplete
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleLocationChange}
                  />
                </Form.Group> */}
                <Form.Group className="mb-3">
                  <Form.Label>{t('postal_code')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h4 className="mb-3 mt-4">{t('vehicle_information')}</h4>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('driver_name')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="driver_name"
                    value={formData.driver_name}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('phone')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('vehicle_number')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="vehicle_number"
                    value={formData.vehicle_number}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('vehicle_type')} *</Form.Label>
                  <Form.Select
                    name="vehicle_type"
                    value={formData.vehicle_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">{t('select_vehicle_type')}</option>
                    <option value="Caddy">Caddy</option>
                    <option value="Bus">Bus</option>
                    <option value="Maxi Bus">Maxi Bus</option>
                    <option value="Planenbus">Planenbus</option>
                    <option value="LKW (7,5t)">LKW (7,5t)</option>
                    <option value="LKW (12t)">LKW (12t)</option>
                    <option value="LKW (18t)">LKW (18t)</option>
                    <option value="LKW (40t)">LKW (40t)</option>
                    <option value="Zugmaschine">Zugmaschine</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('max_weight')} (kg)</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_weight"
                    value={formData.max_weight}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('max_length')} (cm)</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_length"
                    value={formData.max_length}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('max_width')} (cm)</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_width"
                    value={formData.max_width}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('max_height')} (cm)</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_height"
                    value={formData.max_height}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4">
              <Button 
                variant="secondary" 
                className="me-2" 
                onClick={() => navigate('/admin/drivers-company')}
                disabled={saving}
              >
                {t('cancel')}
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('saving')}
                  </>
                ) : (
                  isEditMode ? t('update') : t('save')
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}

export default DriverCompanyForm;